import { apiErrorSchema } from '@academy/shared';

/**
 * Cliente HTTP de la aplicación.
 *
 * ── Por qué `credentials: 'include'` no es opcional ─────────────────────────
 *
 * Ninguna respuesta de esta API devuelve tokens en el cuerpo. La sesión viaja en
 * dos cookies `httpOnly`: el access token (corto) y el refresh token (largo). Con
 * `httpOnly`, el token existe en el navegador pero JavaScript no puede leerlo, de
 * modo que un XSS no puede robarlo.
 *
 * El precio de esa decisión es que el navegador solo manda esas cookies si la
 * petición lleva `credentials: 'include'` Y va al mismo origen. Por eso el
 * frontend nunca llama a `http://localhost:3000`: en desarrollo hay un proxy de
 * Vite y en producción Nginx, ambos sirviendo `/api` en el mismo origen.
 *
 * ── Por qué el refresh se comparte entre peticiones ─────────────────────────
 *
 * El access token dura 15 minutos y el refresh token es de UN SOLO USO: en
 * cuanto se canjea, el anterior queda revocado. Eso obliga a que dos
 * peticiones que caducan a la vez no puedan refrescar por separado:
 *
 *   t=0  GET /api/auth/me   → 401   ┐ dos peticiones del panel,
 *   t=0  GET /api/lessons  → 401   ┘ llegan caducadas a la vez
 *   t=0  POST /refresh #1     → 200, el token viejo queda revocado
 *   t=0  POST /refresh #2     → 204, porque el token ya se usó
 *
 * La segunda revoca la sesión recién creada y el usuario pierde el acceso sin
 * motivo. Por eso todas las peticiones que necesitan refrescar ESPERAN a la
 * misma promesa (`refrescoEnCurso`), y solo una llama a `/auth/refresh`.
 *
 * ── Por qué un fallo de refresh devuelve el error ORIGINAL ────────────────
 *
 * Si `/auth/refresh` falla por un 500 de la base de datos, la causa real es ese
 * 500. Lanzar "no autorizado" escondería que el problema es el servidor y haría
 * que el frontend pareciera que había perdido la sesión.
 */

/** Base de la API. `/api` sirve igual en desarrollo (proxy de Vite) y en producción (Nginx). */
const BASE = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/+$/, '');

/**
 * Error de la API con su código estable.
 *
 * El frontend decide qué hacer por `code`, nunca por el texto de `message`:
 * los mensajes se traducen y cambian de redacción; los códigos no.
 */
export class ApiError extends Error {
  /**
   * @param {{ code: string, message: string, fieldErrors?: Record<string, string[]>, status: number }} datos
   */
  constructor({ code, message, fieldErrors, status }) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fieldErrors = fieldErrors;
  }

  /** @returns {Record<string, string[]>} Errores por campo, para pintar formularios. */
  get fields() {
    return this.fieldErrors ?? {};
  }
}

/** Construye un ApiError a partir de una respuesta fallida. */
async function errorDesde(response) {
  let body = null;
  try {
    body = await response.json();
  } catch {
    // Respuesta sin cuerpo JSON (un 502 de Nginx, por ejemplo). Se usa el
    // estado HTTP como único dato fiable.
  }

  // Se valida contra el contrato antes de fiarse. Si el servidor devolviera una
  // forma inesperada, es preferible un error genérico a propagar un objeto
  // undefined y reventar más arriba con un TypeError inútil.
  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) {
    return new ApiError({ ...parsed.data.error, status: response.status });
  }

  return new ApiError({
    code: 'INTERNAL_ERROR',
    message: `Error ${response.status} del servidor`,
    status: response.status,
  });
}

/**
 * Petición cruda, sin reintentos ni lógica de sesión.
 *
 * @param {string} path Ruta relativa a `/api`, sin el prefijo.
 * @param {{ method?: string, body?: unknown }} [opciones]
 */
async function peticion(path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // Un `fetch` fallido casi siempre es la red, no el servidor: sin internet,
    // el backend apagado, o CORS. Se distingue para poder decir "sin conexión"
    // en vez de "sesión caducada".
    throw new ApiError({
      code: 'INTERNAL_ERROR',
      status: 0,
      message: 'No se pudo contactar con el servidor',
    });
  }

  // 204 y 205 no tienen cuerpo por definición. Intentar leerlo da un error de
  // parseo que no significa nada.
  if (response.status === 204 || response.status === 205) return null;

  if (!response.ok) throw await errorDesde(response);

  return response.json();
}

/**
 * El refresh en curso, si lo hay.
 *
 * Existe UNA sola promesa compartida: mientras esté pendiente, todas las
 * peticiones que necesiten refrescar se enganchan a ella en vez de disparar su
 * propio refresh.
 */
let refrescoEnCurso = null;

/** Canjea el refresh token. Resuelve a `null` (204) si no hay sesión. */
function refrescar() {
  refrescoEnCurso ??= peticion('/auth/refresh', { method: 'POST' }).finally(() => {
    refrescoEnCurso = null;
  });
  return refrescoEnCurso;
}

/**
 * Petición con recuperación de sesión.
 *
 * Si responde 401 con `UNAUTHORIZED` (access token caducado), intenta un refresh
 * y repite UNA vez. Un segundo intento fallido se propaga: si un token recién
 * canjeado tampoco vale, algo va mal y reintentar en bucle sería peor.
 *
 * @param {string} path
 * @param {{ method?: string, body?: unknown, reintentar?: boolean }} [opciones]
 */
async function pedir(path, opciones = {}) {
  const { reintentar = true, ...resto } = opciones;

  try {
    return await peticion(path, resto);
  } catch (error) {
    const caducado =
      error instanceof ApiError && error.status === 401 && error.code === 'UNAUTHORIZED';
    if (!reintentar || !caducado) throw error;

    try {
      await refrescar();
    } catch (errorDeRefresh) {
      // `SESSION_REVOKED` significa que alguien reutilizó un token revocado y
      // se cerraron todas las sesiones. Ese SÍ se propaga, y con él su mensaje:
      // el usuario tiene que enterarse de que sus sesiones se cerraron por
      // seguridad, no de que "ha caducado".
      if (errorDeRefresh instanceof ApiError && errorDeRefresh.code === 'SESSION_REVOKED') {
        throw errorDeRefresh;
      }

      // Cualquier otro fallo (204 = sin sesión, 503 = base caída) se devuelve
      // como estaba: la petición original sigue siendo el problema que la
      // pantalla tiene que mostrar.
      throw error;
    }

    return pedir(path, { ...resto, reintentar: false });
  }
}

/** Envuelve el cuerpo `{ data: ... }` de las respuestas de éxito. */
function datosDe(response) {
  return response?.data ?? null;
}

export const api = {
  /** @returns {Promise<Record<string, unknown> | null>} */
  health: () => pedir('/health'),
  ready: () => pedir('/health/ready'),

  registro: (body) => pedir('/auth/register', { method: 'POST', body }),
  login: (body) => pedir('/auth/login', { method: 'POST', body }),
  forgotPassword: (body) => pedir('/auth/forgot-password', { method: 'POST', body }),
  resetPassword: (body) => pedir('/auth/reset-password', { method: 'POST', body }),
  logout: () => pedir('/auth/logout', { method: 'POST' }),
  logoutEverywhere: () => pedir('/auth/logout-everywhere', { method: 'POST' }),
  me: () => pedir('/auth/me'),

  actualizarPerfil: (body) => pedir('/users/me', { method: 'PATCH', body }),
  cambiarPassword: (body) => pedir('/users/me/password', { method: 'POST', body }),
  borrarCuenta: () => pedir('/users/me', { method: 'DELETE' }),

  temario: () => pedir('/lessons'),
  leccion: (lessonId) => pedir(`/lessons/${encodeURIComponent(lessonId)}`),
  progresoLeccion: (lessonId, status) =>
    pedir(`/lessons/${encodeURIComponent(lessonId)}/progress`, { method: 'PUT', body: { status } }),

  /**
   * El programa público.
   *
   * Va por `pedir` y no por `peticion` a propósito: aunque hoy no necesita
   * sesión, si algún día el servidor exigiera cookie, esta llamada heredaría el
   * refresh del cliente en vez de romperse con un error raro.
   */
  programa: () => pedir('/program'),

  /**
   * El plan de estudios público, por el mismo motivo que `/program`.
   */
  planDeEstudios: () => pedir('/plan-de-estudios'),

  datosDe,
};
